<template>
  <div class="available-products">
    <div class="navbar-background">
      <div class="available-products-nav-bar" data-cy="available-products-nav-bar">
        <h3 class="page-title" data-cy="availability-title">
          {{ $t(`products_availability.title`) }}
        </h3>
        <BreadcrumbMenu
          data-cy="breadcrumb-menu"
          :key="refreshBreadCrumbMenu"
          :crumbs="availabilityBreadcrumbMenuItems" />
        <div class="action-buttons" data-cy="action-buttons">
          <BackstageButton
            :variant="ButtonVariant.SECONDARY"
            data-cy="cancel-button-cnt"
            data-cy-button="cancel-button"
            v-on:click="exit()">
            {{ $t('global.cancel_button') }}
          </BackstageButton>
          <BackstageButton
            v-if="!isQuantityProductsView"
            :disabled="!currentStepActionIsEnabled"
            :icon-right="submitting ? 'loading' : 'arrow-right'"
            :data-cy="currentStep + '-button-cnt'"
            :data-cy-button="currentStep + '-button'"
            v-on:click="executeCurrentStepAction">
            {{ $t(`products_availability.execute_${currentStep}_step_button`) }}
          </BackstageButton>
        </div>
      </div>
    </div>
    <div v-if="showCurrentStepContent" class="current-step-content">
      <ProductsModelsSelector
        v-if="currentStep === 'products-selection'"
        :account-id="selectedAccountId"
        :order-date="formatDateToIsoStringIgnoringTime(getCurrentDate())"
        :pre-selected-product-model-names="allSelectedProductModels"
        hide-select-button
        v-on:selected-product-models="setSelectedProductModels" />
      <ProductsQuantitiesSelector
        v-if="currentStep === 'quantities'"
        :matrix-type="matrixType"
        :account-id="selectedAccountId"
        :default-size="size"
        :selected-product-models="allSelectedProductModels"
        :matrix-orders="matrixOrders"
        :submitting="submitting"
        :minimum-date="getCurrentDate()" />
    </div>
    <div v-else class="loader"></div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import BreadcrumbMenu from '@/components/common/breadcrumb-menu.vue';
import ProductsQuantitiesSelector from '@/components/matrix/products-quantities-selector.vue';
import ProductsModelsSelector from '@/components/matrix/products-models-selector.vue';
import { MatrixOrder } from '@/models/matrix-order.model';
import { MatrixType } from '@/models/matrix-type.enum';
import { accountMixins } from '@/mixins/account.mixins';
import { globalConstants } from '@/constants/global.constants';
import { utilsMixins } from '@/mixins/utils.mixins';
import { type BreadcrumbItem } from '@/models/breadcrumb-item';
import { AvailabilityBreadcrumbSteps } from '@/models/available-products-breadcrumb-steps.enum';
import { useUserStore } from '../stores/user';
import BackstageButton from '../components/common/BackstageButton.vue';
import { storeToRefs } from 'pinia';
import { useFormat } from '../composables/format';
import { ButtonVariant } from '@/models/button-variant.enum';
import { useBPLogger } from '@/composables/business-process-logger';

export default defineComponent({
  name: 'products-availability',
  components: {
    ProductsModelsSelector,
    ProductsQuantitiesSelector,
    BreadcrumbMenu,
    BackstageButton,
  },
  setup() {
    const { isSalesRep } = useUserStore();
    const { selectedAccount, selectedAccountId } = storeToRefs(useUserStore());
    const { formatDateToIsoStringIgnoringTime } = useFormat();
    const { pageViewBPLog } = useBPLogger();

    return {
      isSalesRep,
      selectedAccount,
      selectedAccountId,
      formatDateToIsoStringIgnoringTime,
      ButtonVariant,
      pageViewBPLog,
    };
  },
  data() {
    return {
      submitting: false,
      matrixType: '',
      accountId: '',
      size: undefined as unknown as string,
      matrixOrders: new Array<MatrixOrder>(),
      refreshBreadCrumbMenu: 0,
      currentStep: AvailabilityBreadcrumbSteps.productsModelsSelection,
      selectedProductModels: [] as string[],
      showCurrentStepContent: false,
    };
  },
  computed: {
    isQuantityProductsView(): boolean {
      return this.currentStep === AvailabilityBreadcrumbSteps.productsQuantities;
    },
    availabilityBreadcrumbMenuItems(): BreadcrumbItem[] {
      const items = [
        this.getAvailabilityBreadcrumbMenuItem(AvailabilityBreadcrumbSteps.productsModelsSelection),
        this.getAvailabilityBreadcrumbMenuItem(AvailabilityBreadcrumbSteps.productsQuantities),
      ];

      return items;
    },
    currentStepActionIsEnabled(): boolean {
      switch (this.currentStep) {
        case AvailabilityBreadcrumbSteps.productsModelsSelection:
          return this.selectedProductModels.length > 0;
        case AvailabilityBreadcrumbSteps.productsQuantities:
          return false;
        default:
          return true;
      }
    },
    allSelectedProductModels(): string[] {
      return this.selectedProductModels;
    },
  },
  watch: {
    $route(to) {
      this.currentStep = to.params['step'] ?? AvailabilityBreadcrumbSteps.productsModelsSelection;
      this.refreshBreadCrumbMenu++;
    },
    account: function (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$router.replace({
          params: {
            ...this.$route.params,
            step: AvailabilityBreadcrumbSteps.productsModelsSelection,
          },
        });
      }
    },
  },
  mounted() {
    this.init();
    console.log(this.pageViewBPLog(true, { page_name: 'products-availability' }));
  },
  methods: {
    ...utilsMixins,
    ...accountMixins,
    async init() {
      await this.configureMatrix();
      this.setInitialStep();

      this.showCurrentStepContent = true;
    },
    exit() {
      this.$router.push('/orders');
    },
    async configureMatrix() {
      this.matrixType = MatrixType.availability;
      const defaultSize = this.$t(`config.default_size_run['${this.selectedAccount.billingCountry}']`);

      if (globalConstants.sizeTypes.includes(defaultSize)) {
        this.size = defaultSize;
      }

      this.matrixOrders = [
        {
          date: new Date(),
          selected: true,
          matrixProducts: [],
          total: 0,
        },
      ];
    },
    setInitialStep() {
      const urlStep = this.$route.params['step'] as AvailabilityBreadcrumbSteps;

      if (urlStep === AvailabilityBreadcrumbSteps.productsQuantities && this.allSelectedProductModels.length === 0) {
        return this.$router.replace({
          params: {
            ...this.$route.params,
            step: AvailabilityBreadcrumbSteps.productsModelsSelection,
          },
        });
      }

      this.currentStep = urlStep;
    },
    getAvailabilityBreadcrumbMenuItem(availabilityBreadcrumbItem: AvailabilityBreadcrumbSteps): BreadcrumbItem {
      return {
        id: availabilityBreadcrumbItem,
        name: this.$t(`products_availability.breadcrumb_menu.${availabilityBreadcrumbItem}`).toString(),
        routerPath: `/products-availability/${availabilityBreadcrumbItem}`,
      };
    },
    executeCurrentStepAction() {
      switch (this.currentStep) {
        case AvailabilityBreadcrumbSteps.productsModelsSelection:
          this.filterProductsBasedOnSelectedModels();

          return this.$router.replace({
            params: { ...this.$route.params, step: AvailabilityBreadcrumbSteps.productsQuantities },
          });
      }
    },
    setSelectedProductModels(selectedProductModels: string[]) {
      this.selectedProductModels = selectedProductModels;
    },
    filterProductsBasedOnSelectedModels() {
      this.matrixOrders[0].matrixProducts = this.matrixOrders[0]?.matrixProducts.filter((product) =>
        this.selectedProductModels.includes(product.productGroupId),
      );
    },
    getCurrentDate(): Date {
      return new Date();
    },
  },
});
</script>

<style lang="scss" scoped>
.available-products {
  margin-bottom: 20px;

  .navbar-background {
    @include navbar-background;
  }

  .available-products-nav-bar {
    @include common-container;

    padding: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;

    @include breakpoint(medium) {
      padding-bottom: 10px;
    }

    .page-title {
      font-size: 20px;
      font-weight: 400;
      text-align: left;
    }

    .action-buttons {
      min-width: 160px;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 10px;

      a {
        margin-left: auto;
      }

      :deep() .hidden {
        display: none;
      }
    }
  }
}
</style>
